package com.essntl.features.currency.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.input.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.essntl.core.settings.domain.repository.ConfigurationRepository
import com.essntl.core.settings.ui.AllowedCurrenciesEvent
import com.essntl.core.settings.ui.AllowedCurrenciesViewModel
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.features.currency.domain.model.CurrencyModel
import com.essntl.features.currency.domain.repository.CurrencyRepository
import com.essntl.uikit.components.dropdown.EsFilterDropDownItem
import com.essntl.uikit.components.dropdown.EsFilterDropDownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsSmallIcon
import com.essntl.uikit.components.input.money.EsMoneyInput
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState
import org.koin.compose.koinInject

@Composable
fun EsCurrencyPriceInput(
    value: Double,
    onValueChange: (Double) -> Unit,
    code: String? = null,
    onCodeChange: (String) -> Unit = {},
    label: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default.copy(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false,
        ),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    var currecnies by remember {
        mutableStateOf<List<CurrencyModel>>(emptyList())
    }

    val scope = rememberCoroutineScope()
    val loadingManager = koinInject<LoadingManager>()
    val configurationRepository = koinInject<ConfigurationRepository>()

    val currencyRepository = koinInject<CurrencyRepository>()

    val allowedCurrenciesViewModel = remember(
        scope,
        configurationRepository,
        loadingManager,
    ) {
        AllowedCurrenciesViewModel(
            configurationRepository = configurationRepository,
            currencyRepository = currencyRepository,
            scope = scope,
            loadingManager = loadingManager,
        )
    }

    LaunchedEffect(Unit) {
        allowedCurrenciesViewModel.onEvent(AllowedCurrenciesEvent.GetAllowedCurrencies)

        currencyRepository
            .getAll()
            .onSuccess {
                currecnies = it
            }
    }

    val allowedCurrenciesState by allowedCurrenciesViewModel.state.collectAsState()

    val currentCurrency = remember(
        allowedCurrenciesState.getAllowedCurrencies.data,
        code,
        currecnies,
    ) {
        currecnies.find {
            if (!code.isNullOrEmpty())
                it.code == code
            else
                it.code == allowedCurrenciesState.getAllowedCurrencies.data?.default
        }?.symbol ?: "$"
    }

    EsMoneyInput(
        value = value,
        onValueChange = onValueChange,
        currency = currentCurrency,
        label = label,
        infos = {
            var isCurrenciesExpanded by remember { mutableStateOf(false) }

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(0.5.spacing),
                modifier = Modifier.clickable(
                    enabled = enabled,
                ) {
                    isCurrenciesExpanded = true
                },
            ) {

                Text(
                    text = if (!code.isNullOrEmpty()) code else
                        allowedCurrenciesState.getAllowedCurrencies.data?.default ?: "USD",
                    color = MaterialTheme.esColorScheme.textTertiary,
                    style = MaterialTheme.esTypography.p2Regular14,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.align(Alignment.CenterVertically),
                )

                Column(
                    modifier = Modifier.align(Alignment.CenterVertically),
                ) {
                    EsSmallIcon(
                        res = EsIcons.ArrowDown,
                        tint = MaterialTheme.esColorScheme.textTertiary,
                        modifier = Modifier
                            .clip(CircleShape)
                            .clickable(
                                enabled = enabled,
                            ) {
                                isCurrenciesExpanded = true
                            },
                    )

                    EsFilterDropDownMenu(
                        list = allowedCurrenciesState.getAllowedCurrencies.data?.allowed.orEmpty(),
                        selectedItemIds = setOf(
                            if (!code.isNullOrEmpty())
                                code else
                                allowedCurrenciesState.getAllowedCurrencies.data?.default ?: "USD",
                        ),
                        expanded = isCurrenciesExpanded,
                        onDismissRequest = { isCurrenciesExpanded = false },
                        clearing = false,
                        content = {
                            allowedCurrenciesState.getAllowedCurrencies.data?.allowed
                                .orEmpty()
                                .forEach { allowedCode ->

                                    key(allowedCode) {
                                        EsFilterDropDownItem(
                                            onClick = {
                                                onCodeChange(allowedCode)
                                                isCurrenciesExpanded = false
                                            },
                                            title = allowedCode,
                                            selected = if (!code.isNullOrEmpty())
                                                allowedCode == code else
                                                allowedCode == allowedCurrenciesState.getAllowedCurrencies.data?.default,
                                            modifier = Modifier
                                                .padding(
                                                    vertical = .5f.spacing,
                                                    horizontal = 1.5f.spacing,
                                                ),
                                        )
                                    }
                                }
                        },
                        modifier = Modifier.width(300.dp),
                    )
                }
            }
        },
        message = message,
        prefix = prefix,
        suffix = suffix,
        enabled = enabled,
        readOnly = readOnly,
        state = state,
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        shape = shape,
        modifier = modifier,
    )
}